import { memo, type ReactNode } from "react";
import Button from "@mui/material/Button";
import MuiDialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  /** Label for the always-present dismiss button. Default "Close"; use "Cancel" for forms. */
  closeLabel?: string;
  /** Blocks backdrop/Escape close and disables the close button — pass useDialogState's loading. */
  loading?: boolean;
  /** Primary action buttons rendered before the close button. Full ReactNode — caller owns styling
   *  and disabled/loading state. */
  actions?: ReactNode;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl";
  /** Adds dividers between title, content, and actions. Useful for read-heavy dialogs. */
  dividers?: boolean;
}

/** Reusable dialog shell: title, scrollable content, and an always-present close/cancel button.
 *  Becomes full-screen on mobile. Pass primary actions via the actions slot — rendered before close. */
export const Dialog = memo(function Dialog({
  open,
  onClose,
  title,
  children,
  closeLabel = "Close",
  loading = false,
  actions,
  maxWidth = "md",
  dividers = false,
}: DialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <MuiDialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      fullScreen={fullScreen}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers={dividers}>{children}</DialogContent>
      <DialogActions>
        {actions}
        <Button onClick={onClose} disabled={loading}>
          {closeLabel}
        </Button>
      </DialogActions>
    </MuiDialog>
  );
});
